import * as React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { TextField } from "../ui/TextField";
import { Textarea } from "../ui/Textarea";
import { Select } from "../ui/Select";
import { Checkbox } from "../ui/Checkbox";
import {
  useDeckCheckoutMutation,
  useShippingQuotesMutation,
} from "../../hooks/useShop";
import type { ShippingOption, ShippingService } from "../../api/shop";
import { currencySymbol, formatAmount, usdAmount, formatUsd } from "../../utils/formatPrice";
import { useContent, text, deck, usdRate } from "../../hooks/useContent";
import { COUNTRIES } from "../../data/countries";
import { FormError } from "../ui/FormError";
import { currentReturnPath } from "./deckReturn";

type Props = {
  /** Initial selection: one deck key (upsell offered) or both (bundle). */
  deckKeys: string[];
  open: boolean;
  onClose: () => void;
  portalHost?: HTMLElement | null;
};

const ALL_DECK_KEYS = ["mediumship", "guidance"];

type Form = {
  fullName: string;
  email: string;
  phone: string;
  country: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postcode: string;
  notes: string;
};

const EMPTY: Form = {
  fullName: "", email: "", phone: "", country: "AU", line1: "", line2: "",
  city: "", state: "", postcode: "", notes: "",
};

// Annotations after the AUD amount: the currency code (e.g. AUD), in the price
// color, and — when a rate is set — the informational "≈ $X USD" approximation,
// muted and slightly smaller. Both inline on the price baseline.
function PriceMeta({ cur, usd }: { cur: string; usd: number | null }) {
  return (
    <>
      <span className="ml-1">{cur}</span>
      {usd != null && (
        <span className="ml-2 text-[0.82em] text-primary/60">{formatUsd(usd)}</span>
      )}
    </>
  );
}

export function DeckCheckoutModal({ deckKeys, open, onClose, portalHost }: Props) {
  const { t } = useTranslation("shop");
  const { data: content } = useContent();
  const [form, setForm] = React.useState<Form>(EMPTY);
  const [options, setOptions] = React.useState<ShippingOption[] | null>(null);
  const [service, setService] = React.useState<ShippingService | "">("");
  const [selected, setSelected] = React.useState<string[]>(deckKeys);
  const quotes = useShippingQuotesMutation();
  const checkout = useDeckCheckoutMutation();

  // Array prop identity is not stable across parent renders — depend on the
  // serialized value so a re-created (but equal) array doesn't reset the form.
  const deckKeysKey = deckKeys.join(",");
  React.useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setOptions(null);
      setService("");
      setSelected(deckKeysKey ? deckKeysKey.split(",") : []);
    }
  }, [open, deckKeysKey]);

  const selectedDecks = selected
    .map((k) => deck(content, k))
    .filter((d): d is NonNullable<typeof d> => Boolean(d));
  const isBundle = selected.length > 1;
  // Upsell only when opened for a single tarot deck; the bundle CTA opens with
  // both, and other products (e.g. spirit-box) check out alone — pairing them
  // would wrongly read as the free-shipping deck bundle.
  const showUpsell = deckKeys.length === 1 && ALL_DECK_KEYS.includes(deckKeys[0]);
  const otherKey = ALL_DECK_KEYS.find((k) => k !== deckKeys[0]);
  const other = showUpsell && otherKey ? deck(content, otherKey) : undefined;
  const currency = selectedDecks[0]?.currency ?? "AUD";
  const rate = usdRate(content);
  const otherUsd = other ? usdAmount(other.price, rate) : null;

  const resetQuote = () => {
    setOptions(null);
    setService("");
  };

  const toggleBundle = () => {
    setSelected((prev) =>
      prev.length === 1 && otherKey ? [...prev, otherKey] : deckKeys,
    );
    // Parcel contents changed → any prior quote is stale.
    resetQuote();
  };

  const set = (k: keyof Form) => (v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    // Address changed → invalidate any prior quote.
    resetQuote();
  };

  // Phone and delivery notes don't change the shipping rate, so editing them
  // must not invalidate an existing quote (no resetQuote).
  const setContact = (k: keyof Form) => (v: string) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const addressReady =
    form.fullName && form.email && form.phone && form.line1 && form.city &&
    (form.country !== "AU" || form.postcode);

  const onGetShipping = () => {
    if (!addressReady || quotes.isPending) return;
    quotes.mutate(
      { deckKeys: selected, country: form.country, postcode: form.postcode },
      {
        onSuccess: (opts) => {
          setOptions(opts);
          setService(opts.length === 1 ? opts[0].service : "");
        },
      },
    );
  };

  const onPay = () => {
    if (!service || checkout.isPending) return;
    checkout.mutate(
      {
        deckKeys: selected,
        service: service as ShippingService,
        ...form,
        // Stripe brings the buyer back here, where DeckPaidModal shows the
        // confirmation.
        returnPath: currentReturnPath(),
      },
      {
        onSuccess: ({ url }) => {
          if (url) window.location.href = url;
          else onClose(); // mock mode: no real payment
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={
        isBundle
          ? text(content, "decks.bundle.title")
          : selectedDecks[0]?.title ?? ""
      }
      title={t("title")}
      size="wide"
      container={portalHost ?? null}
      footerLayout="full"
      footer={
        <div className="flex w-full flex-col gap-3 [&>button]:w-full">
          {options && options.length > 0 ? (
            <Button
              variant="solid" surface="light" as="button" type="button"
              disabled={!service || checkout.isPending} onClick={onPay}
            >
              {t("pay")}
            </Button>
          ) : (
            <Button
              variant="solid" surface="light" as="button" type="button"
              disabled={!addressReady || quotes.isPending} onClick={onGetShipping}
            >
              {t("getShipping")}
            </Button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Order summary: one row per selected deck. */}
        <div className="flex flex-col gap-2 md:col-span-2">
          {selectedDecks.map((d) => {
            const usd = usdAmount(d.price, rate);
            return (
              <div key={d.key} className="flex items-center justify-between gap-3">
                <span className="t-body-blue">
                  {d.title} — {d.subtitle}
                </span>
                <span className="t-body-blue whitespace-nowrap">
                  {currencySymbol(d.currency)}{formatAmount(d.price)}
                  <PriceMeta cur={d.currency} usd={usd} />
                </span>
              </div>
            );
          })}
        </div>

        {showUpsell && other && (
          <div className="flex items-center justify-between gap-3 md:col-span-2">
            <Checkbox
              checked={isBundle}
              onChange={toggleBundle}
              label={text(content, "decks.bundle.upsell")}
            />
            <span className="t-body-blue whitespace-nowrap">
              +{currencySymbol(other.currency)}{formatAmount(other.price)}
              <PriceMeta cur={other.currency} usd={otherUsd} />
            </span>
          </div>
        )}

        <div className="md:col-span-2">
          <TextField label={t("fullName")} value={form.fullName} onChange={set("fullName")} requirement="required" />
        </div>
        <TextField label={t("email")} type="email" value={form.email} onChange={set("email")} requirement="required" />
        <TextField label={t("phone")} type="tel" autoComplete="tel" value={form.phone} onChange={setContact("phone")} requirement="required" />
        <Select label={t("country")} value={form.country} onChange={set("country")} options={COUNTRIES} searchable />
        <TextField label={t("postcode")} value={form.postcode} onChange={set("postcode")} requirement={form.country === "AU" ? "required" : "optional"} />
        {/* Street lines span both columns of the desktop grid. */}
        <div className="md:col-span-2">
          <TextField label={t("addressLine1")} value={form.line1} onChange={set("line1")} requirement="required" />
        </div>
        <div className="md:col-span-2">
          <TextField label={t("addressLine2")} value={form.line2} onChange={set("line2")} requirement="optional" />
        </div>
        <TextField label={t("city")} value={form.city} onChange={set("city")} requirement="required" />
        <TextField label={t("stateRegion")} value={form.state} onChange={set("state")} requirement="optional" />
        <div className="md:col-span-2">
          <Textarea
            label={t("notes")}
            value={form.notes}
            onChange={setContact("notes")}
            requirement="optional"
            maxLength={500}
            placeholder={t("notesPlaceholder")}
          />
        </div>

        {options && options.length === 0 && (
          <p role="alert" className="t-body-blue text-error md:col-span-2">
            {t("unshippable")}
          </p>
        )}

        {options && options.length > 0 && (
          <fieldset className="flex flex-col gap-2 border-0 p-0 m-0 md:col-span-2">
            <legend className="t-eyebrow-blue mb-1">{t("shippingMethod")}</legend>
            {options.map((o) => {
              const usd = usdAmount(Number(o.amount), rate);
              return (
              <label key={o.service} className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="flex items-center gap-2">
                  <input
                    type="radio" name="shipping" value={o.service}
                    checked={service === o.service}
                    onChange={() => setService(o.service)}
                  />
                  {o.label}
                </span>
                <span className="t-body-blue whitespace-nowrap">
                  {currencySymbol(currency)}{o.amount}
                  <PriceMeta cur={currency} usd={usd} />
                </span>
              </label>
              );
            })}
          </fieldset>
        )}

        {(quotes.isError || checkout.isError) && (
          <div className="md:col-span-2">
            <FormError
              message={text(content, "decks.checkout.error")}
              onRetry={quotes.isError ? onGetShipping : onPay}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
